import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Tooltip } from '@kontentino/ui';
import clsx from 'clsx';
import routes from 'constants/routes';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import ProfileListSection from 'app/modules/analytics/components/sidebar/ProfileListSection';
import { useReportTemplatesQuery } from 'app/modules/analytics/modules/reports/modules/createReport/hooks/useReportTemplatesQuery';
import { buildAnalyticsReportCreateUrl } from 'app/modules/analytics/modules/reports/utils/analyticsReportsRoutes';
import type { ReportTemplate } from 'api/analytics-reports/types';

interface ReportTemplateRowProps {
  template: ReportTemplate;
  disabled: boolean;
  disabledReason: string;
  onSelect: (code: string) => void;
}

const templateRowClass =
  'tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-gap-1 tw-rounded tw-px-2 tw-py-2 tw-text-left hover:tw-bg-primary-10 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 disabled:hover:tw-bg-transparent';

const ReportTemplateRow: FC<ReportTemplateRowProps> = ({
  template,
  disabled,
  disabledReason,
  onSelect,
}) => {
  const button = (
    <button
      type="button"
      className={clsx(
        templateRowClass,
        'tw-text-grayscale-180 hover:tw-text-primary-100',
      )}
      disabled={disabled}
      onClick={() => onSelect(template.code)}
      data-cy={`analytics_sidebar_report-template-${template.code}`}
    >
      <span className="tw-truncate tw-text-sm tw-font-medium tw-text-inherit">
        {template.label}
      </span>
      {template.description ? (
        <span className="tw-font-normal tw-text-xs tw-leading-4 tw-text-inherit tw-opacity-70">
          {template.description}
        </span>
      ) : null}
    </button>
  );

  if (!disabled) {
    return button;
  }

  return (
    <Tooltip content={disabledReason}>
      <span className="tw-block">{button}</span>
    </Tooltip>
  );
};

const AnalyticsSidebarReportTemplates: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedPageId, selectedGroupId } = useAnalyticsSelection();
  const templatesQuery = useReportTemplatesQuery();

  const hasProfileSelection =
    selectedPageId !== null || selectedGroupId !== null;
  const disabledReason = t('analyticsReports.templates.selectProfileFirst', {
    defaultValue: 'Select a profile to use a template.',
  });

  const handleSelectTemplate = (code: string) => {
    navigate(buildAnalyticsReportCreateUrl(code), {
      state: { returnTo: routes.ANALYTICS_REPORTS },
    });
  };

  return (
    <ProfileListSection
      title={t('analyticsReports.templates.sectionTitle', {
        defaultValue: 'Templates',
      })}
      scrollClassName="tw-max-h-80"
    >
      <div
        className="tw-flex tw-flex-col tw-gap-1"
        data-cy="analytics_sidebar_report-templates"
      >
        {templatesQuery.isLoading ? (
          <>
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
          </>
        ) : templatesQuery.isError ? (
          <p className="tw-px-2 tw-text-xs tw-text-grayscale-100">
            {t('analyticsReports.templates.loadError', {
              defaultValue: 'Could not load templates.',
            })}
          </p>
        ) : (
          (templatesQuery.data ?? []).map((template) => (
            <ReportTemplateRow
              key={template.code}
              template={template}
              disabled={!hasProfileSelection}
              disabledReason={disabledReason}
              onSelect={handleSelectTemplate}
            />
          ))
        )}
      </div>
    </ProfileListSection>
  );
};

export default AnalyticsSidebarReportTemplates;
